import Loader from "components/Loader/Loader";
import { useAppDispatch } from "hooks/hooks";
import { useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { FormatDateShort } from "utils/dateUtils";
import {
  categoryDictionary,
  resourceMaintenanceTypeDictionary,
} from "utils/translationDictionary";
import {
  getOutputForPrint,
  selectState,
  updateOutput,
  updateOutputTemporarily
} from "../output.slice";
import { FormatNumber, FormatNumberWithUnitOrNull } from "utils/numberUtils";
import InfoTooltip from "components/Popup/InfoTooltip";
import { debounce } from "lodash";
import { IConsumablesOutputUpdate } from "Dashboard/scenes/Consumables/domain/IConsumablesOutputUpdate";
import { toast } from "react-toastify";
import { IOutputListItem } from "Dashboard/scenes/Consumables/domain/IOutputListItem";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPrint } from "@fortawesome/free-solid-svg-icons";
import PrintRequisition from "./PrintRequisition";
import PrintBillOfLanding from "./PrintBillOfLanding";
import { IExemption } from "Dashboard/scenes/Consumables/domain/IExemption";
import { selectState as selectStateAuth } from "authentication/main.slice";
import { hasFullAccessTo, hasWriteAccessTo } from "utils/authUtil";
import { CONSUMABLES_MODULE_NAME } from "Dashboard/scenes/Consumables/domain/ConsumablesModule";
import ResourcesSelect from "Dashboard/scenes/Resources/components/ResourcesSelect";
import { useResourceOptions } from "Dashboard/scenes/Resources/hooks/ResourceOptions";
import { styles } from "Dashboard/components/SelectStyle";
import { usePagePrint } from "hooks/usePagePrint";

interface IProps {
  lastTableElementRef?: any;
  handleScroll: (e: React.UIEvent<HTMLDivElement>) => void,
}

const MainSection = ({ lastTableElementRef, handleScroll }: IProps) => {
  const componentRefRequisition = useRef<HTMLDivElement>(null);
  const componentRefBillOfLanding = useRef<HTMLDivElement>(null);
  const [outputIdToPrintRequisition, setOutputIdToPrintRequisition]
    = useState<number | null>(null);
  const [outputIdToPrintBillOfLanding, setOutputIdToPrintBillOfLanding]
    = useState<number | null>(null);

  const dispatch = useAppDispatch();
  const { listItems, totalPrice, totalQuantityAmount, loading } =
    useSelector(selectState);
  const resourceOptions = useResourceOptions();
  const { user } = useSelector(selectStateAuth);

  const onChangeBasicField = (data: IConsumablesOutputUpdate) => {
    dispatch(updateOutputTemporarily(data));
    updateBasicFieldWithDelay(data);
  };

  const updateBasicFieldWithDelay = useMemo(
    () => debounce(async (data: IConsumablesOutputUpdate) => {
      try {
        await dispatch(updateOutput(data)).unwrap();
        // toast.success("Izmena je uspešno zabeležena.");
      } catch (status: any) {
        if (status === 404 || status === 403 || status === 400) {
          toast.error("Izmena nije moguća zbog neočekivane greške");
        }
      }
    }, 1000),
    [dispatch]
  );

  const printRequisition = (outputListItemToPrint: IOutputListItem) => {
    setOutputIdToPrintRequisition(outputListItemToPrint.outputId);
    dispatch(getOutputForPrint(outputListItemToPrint.outputId));
  };

  const doPrintRequisition = (outputListItemToPrint: IExemption | undefined) => {
    if (outputIdToPrintRequisition === outputListItemToPrint?.exemptionId) {
      handlePrintRequisition();
      setOutputIdToPrintRequisition(null);
    }
  }

  const handlePrintRequisition = usePagePrint(
    componentRefRequisition, { landscape: true, margins: "0" });

  const printBillOfLanding = (outputListItemToPrint: IOutputListItem) => {
    setOutputIdToPrintBillOfLanding(outputListItemToPrint.outputId);
    dispatch(getOutputForPrint(outputListItemToPrint.outputId));
  };

  const doPrintBillOfLanding = (outputListItemToPrint: IExemption | undefined) => {
    if (outputIdToPrintBillOfLanding === outputListItemToPrint?.exemptionId) {
      handlePrintBillOfLanding();
      setOutputIdToPrintBillOfLanding(null);
    }
  };

  const handlePrintBillOfLanding = usePagePrint(
    componentRefBillOfLanding, { landscape: true, margins: "0" });

  return (
    <main className="consumables-output">
      <section>
        <div className="g-grid g-header">
          <div className="row">
            <div className="col separator-line"></div>
            <div className="col lowercase center separator-line">br. otpr-rač</div>
            <div className="col capitalize left separator-line">Ime</div>
            <div className="col lowercase center separator-line">kategorija</div>
            <div className="col lowercase center separator-line">dobavljač</div>
            <div className="col lowercase center separator-line">količina iz</div>
            <div className="col lowercase center separator-line">cena izdatog</div>
            <div className="col lowercase center separator-line">datum izlaza</div>
            <div className="col lowercase center separator-line">za koji resurs</div>
            <div className="col lowercase center separator-line">za štampu</div>
            <div className="col lowercase center separator-line">usluga</div>
            <div className="col lowercase left separator-line">napomena</div>
          </div>
        </div>
        <div className="g-grid g-content" onScroll={handleScroll}>
          {listItems &&
            listItems.length > 0 &&
            listItems.map(
              (listItem, listItemIndex) => {
                const isLast = listItemIndex === listItems.length - 1;
                return (
                  <div
                    className="row"
                    key={listItem.outputItemId}
                    ref={isLast ? lastTableElementRef : undefined}
                  >
                    <div className="col">
                      {listItem.orderNumberWithinOutput === 1 && (
                        <div className="bg-item bg-transparent center">
                          {listItem.billOfLandingId ? (
                            <InfoTooltip tooltip={`Štampaj otpremnicu ${listItem.billOfLandingId}`} element="span"
                              onClick={() => printBillOfLanding(listItem)}>
                              <FontAwesomeIcon fontSize={13} icon={faPrint} />
                            </InfoTooltip>
                          ) : (<span className="marginPrintFix"></span>)}
                          {listItem.requisitionId ? (
                            <InfoTooltip tooltip={`Štampaj trebovanje ${listItem.requisitionId}`} element="span"
                              onClick={() => printRequisition(listItem)}>
                              <FontAwesomeIcon fontSize={13} icon={faPrint} />
                            </InfoTooltip>
                          ) : (<span className="marginPrintFix"></span>)}
                          <span className="xxx">
                            <input type="checkbox" checked={listItem.xxx} readOnly />
                            <span className="special-ch"></span>
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="col light">
                      <InfoTooltip tooltip={`${listItem.inputBillOfLandingId}`} classesProp="bg-item center">
                        {listItem.inputBillOfLandingId}
                      </InfoTooltip>
                    </div>
                    <div className="col strong uppercase">
                      <div className="bg-item overflowMinWidth left">
                        <InfoTooltip tooltip={listItem.name} classesProp="bg-text-overflow">
                          {listItem.name}
                        </InfoTooltip>
                      </div>
                    </div>
                    <div className="col left light">
                      <div className="bg-item">
                        {categoryDictionary[listItem.category]}
                      </div>
                    </div>
                    <div className="col left uppercase light">
                      <InfoTooltip tooltip={listItem.supplier} classesProp="bg-item">
                        {listItem.supplier}
                      </InfoTooltip>
                    </div>
                    <div className="col strong danger">
                      <div className="bg-item right">
                        {FormatNumber(listItem.quantityAmount)}
                      </div>{" "}
                    </div>
                    <div className="col strong">
                      <div className="bg-item overflowMinWidth right">
                        <InfoTooltip
                          tooltip={FormatNumberWithUnitOrNull(listItem.totalPrice, "rsd") ?? undefined}
                          classesProp="bg-text-overflow">
                          {FormatNumberWithUnitOrNull(listItem.totalPrice, "rsd")}
                        </InfoTooltip>
                      </div>
                    </div>
                    <div className="col light">
                      <div className="bg-item center">
                        {FormatDateShort(listItem.date)}
                      </div>
                    </div>
                    <div className="col light">
                      {hasFullAccessTo(user, CONSUMABLES_MODULE_NAME)
                        ? (
                          <ResourcesSelect
                            defaultValue={listItem.resource?.resourceId}
                            options={resourceOptions}
                            customStyles={styles.dropdownInTable}
                            onChange={(value: any) =>
                              updateBasicFieldWithDelay({
                                outputItemId: listItem.outputItemId,
                                resourceId: value
                              })}
                          />
                        )
                        :
                        <div className="bg-item overflowMinWidth">
                          <InfoTooltip tooltip={listItem.resource.nameWithIdentifier}
                            classesProp="bg-text-overflow">
                            {listItem.resource.nameWithIdentifier}
                          </InfoTooltip>
                        </div>
                      }
                    </div>
                    <div className="col">
                      <div className="bg-item bg-item-border-light">
                        <ResourcesSelect
                          defaultValue={listItem.resourceOverride?.resourceId}
                          options={resourceOptions}
                          customStyles={styles.dropdownInTable}
                          onChange={(value: any) =>
                            updateBasicFieldWithDelay({
                              outputItemId: listItem.outputItemId,
                              resourceOverrideId: value
                            })}
                        />
                      </div>
                    </div>
                    <div className="col light">
                      <div className="bg-item center">
                        {resourceMaintenanceTypeDictionary[listItem.resourceMaintenanceType]}
                      </div>
                    </div>
                    <div className="col left">
                      {hasWriteAccessTo(user, CONSUMABLES_MODULE_NAME)
                        ? (
                          <input className="bg-item bg-transparent" type="text"
                            value={listItem.note ?? ""}
                            onChange={(e) => {
                              onChangeBasicField({
                                outputItemId: listItem.outputItemId,
                                note: e.target.value
                              })
                            }} />
                        )
                        : (
                          <span className="bg-item bg-transparent">{listItem.note}</span>
                        )
                      }
                    </div>
                  </div>
                );
              }
            )}
        </div>

        <div className="g-grid g-footer">
          {loading && <Loader />}
          <div className="row">
            <div></div>
            <div></div>
            <div></div>
            <div></div>
            <div></div>
            <div className="col strong danger text-medium">
              <span className="bg-item right">
                {FormatNumber(totalQuantityAmount)}
              </span>
            </div>
            <div className="col strong total text-medium">
              <span className="bg-item right">
                {FormatNumber(totalPrice)} rsd
              </span>
            </div>
            <div></div>
            <div></div>
            <div></div>
            <div></div>
            <div></div>
            <div></div>
          </div>
        </div>
      </section>
      <div ref={componentRefRequisition} className="print-area">
        <PrintRequisition doPrint={doPrintRequisition} />
      </div>
      <div ref={componentRefBillOfLanding} className="print-area">
        <PrintBillOfLanding doPrint={doPrintBillOfLanding} />
      </div>
    </main>
  );
};

export default MainSection;
